<script setup>
import { computed, ref, watch } from 'vue'
import { cloneDeep } from 'lodash'
import FormCreator from '../UI/Form/FormCreator.vue'
import SimulationHeadband from './SimulationHeadband.vue'
import BlockContent from '../UI/Content/BlockContent.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import AddDocumentButtonAndModal from '../Document/AddDocumentButtonAndModal.vue'
import CommissionList from '../Finance/CommissionList.vue'
import EquipmentList from '../Equipment/EquipmentList.vue'
import ServiceList from '../Service/ServiceList.vue'
import useUser from '../../../../Composables/User/useUser.ts'
import useConfig from '../../../../Composables/useConfig'

const props = defineProps({
  simulationId: { type: Number, required: true },
  simulation: { type: Object, required: true },
  def: { type: Object, required: true },
})

const emit = defineEmits(['set-simulation', 'simulation-changed'])

const { isReadOnly, hasRole } = useUser()
const { isTheme, hasFeature } = useConfig()

const sending = ref(false)
const build = ref(false)
const formData = ref(null)

const formService = ref(null)
const documentList = ref(null)

const simulationState = ref(cloneDeep(props.simulation || {}))

watch(
  () => props.simulation,
  (value) => {
    simulationState.value = cloneDeep(value || {})
  },
  { deep: true },
)

const canSeeListService = computed(() => {
  if (isReadOnly.value && hasFeature('services') && simulationState.value?.finance?.services?.length > 0) {
    return true
  }

  return !isReadOnly.value && hasFeature('services') && simulationState.value.finance
})

const canSeeListCommission = computed(() => {
  if (!hasFeature('commissions')) {
    return false
  }

  if (
    isReadOnly.value &&
    simulationState.value.finance &&
    simulationState.value.finance.commissions &&
    simulationState.value.finance.commissions.length > 0 &&
    !hasRole('ROLE_PROFITABILITY_SHOW')
  ) {
    return true
  }

  return !isReadOnly.value && simulationState.value.finance && !hasRole('ROLE_PROFITABILITY_SHOW')
})

const displayServices = computed(() => {
  return simulationState.value.finance.services.filter(
    (service) => service.serviceType.serviceCategory.display === true && service.active === true,
  )
})

function updateData(newData) {
  formData.value = newData
}

function globalBuild({ build: buildValue }) {
  build.value = buildValue
}

function objectReturn({ sending: sendingValue, object }) {
  sending.value = sendingValue
  if (object) {
    emit('set-simulation', object)
  }
}

function equipmentSent(e) {
  if (e.id === simulationState.value.equipment.id) {
    simulationState.value.equipment = e
  } else {
    let equipmentIndex = simulationState.value.equipment.equipments.findIndex((eq) => eq.id === e.id)
    if (equipmentIndex === -1) {
      simulationState.value.equipment.equipments.push(e)
    } else {
      simulationState.value.equipment.equipments.splice(equipmentIndex, 1, e)
    }
  }
  emit('simulation-changed')
}

function servicesSent(services) {
  emit('set-simulation', {
    ...simulationState.value,
    finance: {
      ...simulationState.value.finance,
      services: services,
    },
  })
}

function commissionsSent(commissions) {
  emit('set-simulation', {
    ...simulationState.value,
    finance: {
      ...simulationState.value.finance,
      commissions: commissions,
    },
  })
}

const quantityService = computed(() => {
  let quantity = 1
  if (simulationState.value && simulationState.value.equipment && simulationState.value.equipment.equipments) {
    quantity += simulationState.value.equipment.equipments.length
  }
  return quantity
})

function updateSimulation() {
  try {
    formService.value?.methods('patch')
  } catch (err) {}
}

function finalizeDemand() {
  if (simulationState.value.deliveryAt === null && isTheme('olinn')) {
    updateSimulation()
  }
  window.location.href = '/devis/simulation.php?i=' + props.simulationId + '&p=demand&index=simulation'
}

function afterUpload(document) {
  const allowedTypes = ['import_decision', 'equipment_import', 'service_import', 'pdf_decision']
  if (allowedTypes.includes(document.documentType.internalName)) {
    location.reload()
  }
  try {
    if (documentList.value) {
      documentList.value.getDocuments()
    }
  } catch (err) {}
}

function removeService(service) {
  simulationState.value.finance.services = simulationState.value.finance.services.filter(({ id }) => id !== service.id)
}
</script>
<template>
  <div>
    <FormCreator
      :id="simulationId"
      :ref="formService"
      :config-def="def"
      :config-object="simulation"
      :params="{ simulationId: simulationId }"
      src-error="formservice"
      endpoint="quotations"
      method="patch"
      dynamic
      class-to-add="d-none"
      @global-build="globalBuild"
      @object-return="objectReturn"
      @update-data="updateData"
    />
    <div v-if="build">
      <div class="row">
        <div class="col-md-12">
          <SimulationHeadband :simulation="simulation" :def="def" />
        </div>
      </div>

      <BlockContent v-if="simulation.equipment" e2e-block="equipmentList">
        <template #header-title>
          <div class="row">
            <div class="col-md-5">
              <h4 class="text-">{{ $t('contract.list.table.equipment_detail') }}</h4>
            </div>
            <div v-if="!isReadOnly" class="col-md-7">
              <FieldCreator
                :form-data="formData"
                name="deliveryAt"
                form-ref="formservice"
                domain="contract"
                label-left-equal
                refresh
                @get-date-change="updateSimulation"
              />
            </div>
            <div v-else class="col-md-6">
              <p class="fs-16 form-label-font require col-xl-4 col-md-12">
                {{ $t('simulation.fields.deliveryAt.label') }}: {{ simulation.deliveryAt }}
              </p>
            </div>
          </div>
        </template>
        <EquipmentList
          context="simulation"
          :main-equipment="simulation.equipment"
          :equipments="simulation.equipment.equipments"
          :total="Number(simulation.finance.simulationAmount)"
          :outstanding="simulation.finance.outstanding"
          :max-rent="
            simulation.decision.rent ? Number(simulation.decision.rent.amount) : Number(simulation.finance.amount)
          "
          :simulation-id="simulationId"
          :suppliers="def.supplier"
          :platform-param-id="simulation.platformParam.id"
          :disabled="simulation.hasContract"
          @equipment-sent="equipmentSent"
          @equipment-deleted="$emit('simulation-changed')"
        />
        <div v-if="!isReadOnly && hasRole('ROLE_MODEL_AND_IMPORT_SHOW')" class="row justify-content-center">
          <AddDocumentButtonAndModal
            id="equipment"
            :ref="documentList"
            context="Simulation"
            :context-id="simulationId"
            internalname="equipment_import"
            :disabled="simulation.hasContract"
            @document-uploaded="afterUpload"
          />
        </div>
      </BlockContent>

      <div v-if="canSeeListService && simulation.equipment.productType.hasServices">
        <BlockContent title-t="service.title" e2e-block="serviceList">
          <ServiceList
            :contract-id="0"
            :finance-id="simulation.finance.id"
            :services="displayServices"
            :quantity="quantityService"
            :readonly="!hasRole('ROLE_EDIT_SIMULATION_SERVICES')"
            :disabled="simulation.hasContract"
            @services-sent="servicesSent"
            @service-removed="removeService"
          />
          <div v-if="!isReadOnly && hasRole('ROLE_MODEL_AND_IMPORT_SHOW')" class="row justify-content-center">
            <AddDocumentButtonAndModal
              id="service"
              :ref="documentList"
              context="Simulation"
              :context-id="simulationId"
              internalname="service_import"
              :disabled="simulation.hasContract"
              @document-uploaded="afterUpload"
            />
          </div>
        </BlockContent>
      </div>

      <div v-if="canSeeListCommission" class="row">
        <div class="col-md-12">
          <BlockContent
            title-t="commission.title"
            :title-level="5"
            color="info"
            inner-bloc-class="border-contract-blue"
            e2e-block="commissionList"
          >
            <CommissionList
              ref="commissions"
              :finance-id="simulation.finance.id"
              :commissions="simulation.finance.commissions"
              @commissions-sent="commissionsSent"
            />
          </BlockContent>
        </div>
      </div>

      <div v-if="!isReadOnly && simulation.status != 'CANCELED'" class="d-flex justify-content-center">
        <button class="btn btn-primary" e2e-button="simulationFinalize" @click="finalizeDemand">
          {{ $t('simulation.popin.finalize.button') }}
        </button>
      </div>
    </div>

    <div v-else class="row">
      <div class="col-md-12">
        <BlockContent>
          <div class="row d-flex">
            <div class="col col-md-4 flex-fill">
              <h4>{{ $t('contract.information.menu_params', { data: $t('contract.information.contributor') }) }}</h4>
              <div class="row py-3 mb-3 mx-3 loading"></div>
              <div class="row py-3 mb-3 mx-3 loading"></div>
              <div class="row py-3 mb-3 mx-3 loading"></div>
              <div class="row py-3 mb-3 mx-3 loading"></div>
            </div>
            <div class="col col-md-4 flex-fill border-information">
              <h4>{{ $t('contract.information.menu_params', { data: $t('contract.list.table.equipment') }) }}</h4>
              <div class="row py-3 mb-3 mx-3 loading"></div>
              <div class="row py-3 mb-3 mx-3 loading"></div>
              <div class="row py-3 mb-3 mx-3 loading"></div>
              <div class="row py-3 mb-3 mx-3 loading"></div>
            </div>
            <div class="col col-md-4 flex-fill">
              <h4>{{ $t('contract.information.menu_params', { data: $t('contract.information.prestation') }) }}</h4>
              <div class="row py-3 mb-3 mx-3 loading"></div>
              <div class="row py-3 mb-3 mx-3 loading"></div>
              <div class="row py-3 mb-3 mx-3 loading"></div>
              <div class="row py-3 mb-3 mx-3 loading"></div>
            </div>
          </div>
        </BlockContent>
      </div>
    </div>
  </div>
</template>
